import { BarChart2, ChevronDown, ChevronUp, Search, XCircle } from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';

import {
  GetTeamFiltersQuery as GetTeamFilters,
} from '__generated__/graphql';

import { isEmpty } from '../../../common/helpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetTeamsFiltersQuery from './GetTeamFiltersQuery';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export type TeamStats = GetTeamFilters['project']['team_stats'];

export interface TeamFilterParams {
  event?: string | null;
  search?: string | null;
  min_size?: number | null;
  max_size?: number | null;
}

export const defaultFilters: TeamFilterParams = {
  event: null,
  search: null,
  min_size: null,
  max_size: null,
};

/**
 * Types of filters that can be applied to teams.
 * */
type TeamFilterType = (
  'event' | 'search' | 'size'
);

export interface TeamFiltersProps {
  types?: TeamFilterType[];
  filters: TeamFilterParams;
  onChange: (update: (filters: TeamFilterParams) => TeamFilterParams) => void;
  onStatsChange?: (stats: TeamStats) => void;
  readOnly?: boolean;
  showResetButton?: boolean;
}

const TeamFilters = ({
  types,
  filters: filtersProp,
  onChange,
  onStatsChange,
  readOnly = false,
  showResetButton = true,
}: TeamFiltersProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  const enabled = (type: TeamFilterType) => !types || types.includes(type);

  const filters = {
    ...defaultFilters,
    ...filtersProp,
  };

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the stats
  const { data, loading } = useQuery(
    GetTeamsFiltersQuery,
    {
      variables: {
        ...omit(filters, 'event'),
        events: filters.event ? [filters.event] : [],
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        onStatsChange?.(data.project.team_stats);
      },
    },
  );

  const stats = data?.project.team_stats;

  const getDefaultShowingFilters = (filters: TeamFilterParams) => ({
    size: !!filters.min_size || !!filters.max_size,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  /**
   * Handles changes to the min size input by storing the value in the filters.
   */
  const handleMinSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      min_size: value ? parseInt(value, 10) : null,
    }));
  };

  /**
   * Handles changes to the min size input by storing the value in the filters.
   */
  const handleMaxSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      max_size: value ? parseInt(value, 10) : null,
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    onChange((filters) => ({
      ...defaultFilters, // Reset all filters except search.
      search: filters.search,
      event: event?.id,
    }));
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: event.target.value || null }));
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: null }));

    searchRef.current.value = '';
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, UI state, and search input.
   */
  const resetFilters = (filters?: TeamFilterParams) => {
    const newFilters = { ...defaultFilters, ...filters };

    onChange(() => newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters.search || '';
    }
  };

  return (
    <UI.FormGrid sc={{ gutter: 0.75 }}>
      {enabled('event') && (
        <UI.Div>
          <EventPicker
            eventId={filters.event}
            onChange={selectEvent}
            renderActive={(event) => (
              <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                {event?.title || t('all_events')}
              </UI.Span>
            )}
          />
        </UI.Div>
      )}

      {enabled('search') && (
        <UI.InputGroup>
          <UI.InputWrapper>
            <UI.Span>
              <UI.Icon>
                <Search />
              </UI.Icon>
            </UI.Span>
            <UI.DebouncedInput
              type="text"
              value={filters.search}
              onChange={handleSearch}
              placeholder={t('search_teams')}
              ref={searchRef}
              sc={{ px: 5 }}
              disabled={readOnly}
            />
            {filters.search && (
              <UI.Span>
                <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                </UI.A>
              </UI.Span>
            )}
          </UI.InputWrapper>
        </UI.InputGroup>
      )}

      {enabled('size') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('size')}
              role="button"
              sc={{ mb: showingFilters.size ? 2 : 0 }}
            >
              {t('team_size')}
              {' '}
              {!showingFilters.size && (filters.min_size || filters.max_size) && (
                <UI.Badge>
                  {filters.min_size || 0}
                  {filters.max_size && <>&ndash;</>}
                  {!filters.max_size && '+'}
                  {filters.max_size}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.size && <ChevronUp />}
                {!showingFilters.size && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.size && (
              <UI.FormGrid sc={{ columns: '1fr 1fr' }} style={{ maxWidth: 200 }}>
                <UI.Div>
                  <UI.InputLabel htmlFor="MinSize">
                    {t('min')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.min_size}
                    onChange={handleMinSizeChange}
                    maxLength={4}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MinSize"
                  />
                </UI.Div>
                <UI.Div>
                  <UI.InputLabel htmlFor="MaxSize">
                    {t('max')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.max_size}
                    onChange={handleMaxSizeChange}
                    maxLength={4}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MaxSize"
                  />
                </UI.Div>
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        </>
      )}

      <UI.HR style={{ borderStyle: 'dotted' }} />

      <UI.Div sc={{ loading }}>
        <UI.Div sc={{ mb: 2 }}>
          <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
            <UI.Icon sc={{ mr: 1 }}>
              <BarChart2 />
            </UI.Icon>
            {t('in_this_selection')}
          </UI.Span>
        </UI.Div>
        <UI.FlexContainer>
          <UI.Div sc={{ mr: 3 }} style={{ minWidth: 55 }}>
            <UI.H4>{formatNumber(stats?.teams || 0)}</UI.H4>
            <UI.Small>{t(stats?.teams === 1 ? 'team' : 'teams')}</UI.Small>
          </UI.Div>
          <UI.Div sc={{ mr: 3 }} style={{ minWidth: 55 }}>
            <UI.H4>{formatNumber(stats?.registrations || 0)}</UI.H4>
            <UI.Small>{t(stats?.registrations === 1 ? 'team_member' : 'team_members')}</UI.Small>
          </UI.Div>
        </UI.FlexContainer>
      </UI.Div>

      {showResetButton && hasFilters && (
        <UI.Div>
          <UI.Button onClick={() => resetFilters()}>
            {t('reset_filters')}
          </UI.Button>
        </UI.Div>
      )}
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export default TeamFilters;
